import { useState } from 'react';
import { Menu, MenuItem } from '@mui/material';

import {
  Divider,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  MoreVertIcon,
} from '@occc-app/design-system';
import {
  RetailerNetworkUserFragment,
  Retailer_Network_User_Role,
  RetailerNetworkUserUpdate,
} from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { RemoveUserFromRetailerNetwork } from '@occc-app/App/PolicyBasedAccessControl/userActions';
import { formatPhoneNumberNationalForUS } from '@occc/utils-phone-number';

import { userRolesDropdownOptions } from './retailer-network-users-configuration.constants';
import { useGetIsUserPermittedToEditRole } from './hooks/use-get-is-user-permitted-to-edit-role.hook';
import { useGetIsUserPermittedToRemoveUserFromNetwork } from './hooks/use-get-is-user-permitted-to-remove-user-from-network.hook';

import RetailerNetworkUserTableChangeRoleDropdown from '../components/retailer-network-user-table-change-role-dropdown.component';

const mapUserRoleToText = {
  [Retailer_Network_User_Role.RetailerNetworkAdmin]: 'Network Admin',
  [Retailer_Network_User_Role.RetailerNetworkUser]: 'Network User',
};

type MenuItem = 'userDetails' | 'removeFromNetwork';

type RetailerNetworkTableRowProps = {
  item: RetailerNetworkUserFragment;
  onRemoveItemClick: () => void;
  handleUpdateRetailerNetworkUserRole: (input: RetailerNetworkUserUpdate) => Promise<void>;
  isRetailerNetworkUserUpdateInProgress: boolean;
  handleRedirectToUserDetailsPage: (userUuid: string) => void;
  retailerNetworkUuid: string;
};

export const RetailerNetworkUsersTableRow = ({
  item,
  onRemoveItemClick,
  handleUpdateRetailerNetworkUserRole,
  isRetailerNetworkUserUpdateInProgress,
  handleRedirectToUserDetailsPage,
  retailerNetworkUuid,
}: RetailerNetworkTableRowProps) => {
  const [actionsForUuid, setActionsForUuid] = useState<string | null>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isUserPermittedToEditRole = useGetIsUserPermittedToEditRole(retailerNetworkUuid);
  const isRemoveFromNetworkButtonVisible = useGetIsUserPermittedToRemoveUserFromNetwork();

  const { name, emailAddress, phoneNumber, role, uuid } = item;

  const isTargetUserRetailerNetworkManagerRole =
    role === Retailer_Network_User_Role.RetailerNetworkAdmin ||
    role === Retailer_Network_User_Role.RetailerNetworkUser;

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    setActionsForUuid(null);
  };

  const getHandlerForAdditionalActions =
    (uuid: string) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(uuid);
    };

  const getMenuItemAction = (menuItemName: MenuItem) => (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (menuItemName === 'userDetails') {
      handleRedirectToUserDetailsPage(uuid);
    }

    if (menuItemName === 'removeFromNetwork') {
      onRemoveItemClick();
    }

    handleClose(event);
  };

  const onRowClick = () => {
    handleRedirectToUserDetailsPage(uuid);
  };

  return (
    <TableRow key={uuid} onClick={onRowClick}>
      <TableCell>{name}</TableCell>
      <TableCell>{emailAddress}</TableCell>
      <TableCell>{formatPhoneNumberNationalForUS(phoneNumber || '')}</TableCell>
      <TableCell>
        {isUserPermittedToEditRole ? (
          <RetailerNetworkUserTableChangeRoleDropdown
            userRole={role}
            userRolesOptions={userRolesDropdownOptions}
            onRoleChange={(userRole) =>
              handleUpdateRetailerNetworkUserRole({
                retailerNetworkUserUuid: uuid,
                role: userRole,
              })
            }
            isRetailerNetworkUserUpdateInProgress={isRetailerNetworkUserUpdateInProgress}
          />
        ) : (
          <Typography variant='body2'>
            {isTargetUserRetailerNetworkManagerRole ? mapUserRoleToText[role] : ''}
          </Typography>
        )}
      </TableCell>
      <TableCell align='right'>
        <IconButton onClick={getHandlerForAdditionalActions(uuid)} sx={{ p: 0 }}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          open={uuid === actionsForUuid}
          onClose={handleClose}
          anchorEl={anchorEl}
          PaperProps={{
            sx: {
              borderRadius: '4px',
            },
          }}
        >
          <MenuItem onClick={getMenuItemAction('userDetails')}>
            View Retailer Network User Details
          </MenuItem>
          <Can I={RemoveUserFromRetailerNetwork({ uuid: retailerNetworkUuid })}>
            {/* After BE will add ability to check roles in policy, we can remove this condition */}
            {isRemoveFromNetworkButtonVisible && [
              <Divider key='divider' />,
              <MenuItem key='removeFromNetwork' onClick={getMenuItemAction('removeFromNetwork')}>
                Remove from Network
              </MenuItem>,
            ]}
          </Can>
        </Menu>
      </TableCell>
    </TableRow>
  );
};
